import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../context/ToastContext';
import API from '../api/axios';
import CategorizedSetsDisplay from '../components/CategorizedSetsDisplay';
import { Icon } from '../components/SvgIcons';
import './UniversityPoolCOE.css';

const UniversityPoolCOE = () => {
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState('pools');
  const [poolsReady, setPoolsReady] = useState([]);
  const [selectedPool, setSelectedPool] = useState(null);
  const [availableSets, setAvailableSets] = useState([]);
  const [setsByCategory, setSetsByCategory] = useState({ general: [], special: [], reexam: [] });
  const [selectedSets, setSelectedSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [showSetModal, setShowSetModal] = useState(false);
  const [selectedSetDetails, setSelectedSetDetails] = useState(null);
  const [finalizedPools, setFinalizedPools] = useState([]);
  const [showEncryptionModal, setShowEncryptionModal] = useState(false);
  const [encryptionPassword, setEncryptionPassword] = useState('');
  const [downloadingPDF, setDownloadingPDF] = useState({});
  const [pendingDownloadData, setPendingDownloadData] = useState(null);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [commentsText, setCommentsText] = useState('');
  const [pendingSetSelection, setPendingSetSelection] = useState(null);
  const [editingSetDetails, setEditingSetDetails] = useState(null);
  const [isEditingSet, setIsEditingSet] = useState(false);

  const fetchPoolsReady = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/university-pool-coe/pools-ready');

      if (data.success) {
        setPoolsReady((data.pools || []).filter(p => p.status !== 'completed'));
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to fetch pools ready for COE review', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const fetchFinalizedPools = useCallback(async () => {
    try {
      const { data } = await API.get('/university-pool-coe/pools-ready');

      // Filter only finalized pools (status = 'completed')
      const finalized = data.pools?.filter(p => p.status === 'completed') || [];
      setFinalizedPools(finalized);
    } catch (err) {
      console.error('Error fetching finalized pools:', err);
    }
  }, []);

  useEffect(() => {
    fetchPoolsReady();
    fetchFinalizedPools();
  }, [fetchPoolsReady, fetchFinalizedPools]);

  const fetchPoolSets = async (poolId) => {
    try {
      setLoading(true);
      const { data } = await API.get(`/university-pool-coe/pools/${poolId}/sets`);

      if (data.success) {
        setAvailableSets(data.sets || []);
        setSetsByCategory(data.sets_by_category || { general: [], special: [], reexam: [] });
        setSelectedPool(poolId);
        setActiveTab('review');
        fetchSelectedSets(poolId);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to fetch pool sets', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSelectedSets = async (poolId) => {
    try {
      const { data } = await API.get(`/university-pool-coe/pools/${poolId}/selected-sets`);

      if (data.success) {
        setSelectedSets(data.selections || []);
      }
    } catch (err) {
      console.error('Failed to fetch selected sets:', err);
    }
  };

  const handleSelectSet = async (setId, selectionOrder, comments = '') => {
    try {
      const { data } = await API.post(`/university-pool-coe/pools/${selectedPool}/sets/${setId}/select`, {
        selection_order: selectionOrder,
        comments
      });

      if (data.success) {
        showToast(`Set selected as option ${selectionOrder}`, 'success');
        fetchPoolSets(selectedPool);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to select set', 'error');
    }
  };

  const handleUnselectSet = async (setId) => {
    try {
      const { data } = await API.delete(`/university-pool-coe/pools/${selectedPool}/sets/${setId}/unselect`);

      if (data.success) {
        showToast('Set unselected successfully', 'success');
        fetchPoolSets(selectedPool);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to unselect set', 'error');
    }
  };

  const handleFinalizePaper = async () => {
    try {
      const { data } = await API.post(`/university-pool-coe/pools/${selectedPool}/finalize`);

      if (data.success) {
        showToast('Paper finalized successfully', 'success');
        setShowFinalizeModal(false);
        fetchPoolsReady();
        fetchFinalizedPools();
        fetchPoolSets(selectedPool);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to finalize paper', 'error');
    }
  };

  const viewSetDetails = async (setId) => {
    try {
      const { data } = await API.get(`/university-pool-coe/pools/${selectedPool}/sets/${setId}`);

      if (data.success) {
        setSelectedSetDetails(data.set);
        setEditingSetDetails(JSON.parse(JSON.stringify(data.set))); // Deep copy for editing
        setIsEditingSet(false);
        setShowSetModal(true);
      }
    } catch (err) {
      showToast('Failed to fetch set details', 'error');
    }
  };

  const handleEditSetQuestion = (subQuestionId, field, value) => {
    if (!editingSetDetails || !editingSetDetails.questions) return;

    const updatedQuestions = { ...editingSetDetails.questions };
    if (updatedQuestions[subQuestionId]) {
      if (field === 'question_text') {
        updatedQuestions[subQuestionId].variation.question_text = value;
      } else if (field === 'correct_answer') {
        updatedQuestions[subQuestionId].variation.correct_answer = value;
      } else if (field === 'marks') {
        updatedQuestions[subQuestionId].variation.marks = value;
      } else if (field.startsWith('option_')) {
        const optIndex = parseInt(field.split('_')[1]);
        if (!updatedQuestions[subQuestionId].variation.options) {
          updatedQuestions[subQuestionId].variation.options = [];
        }
        const options = Array.isArray(updatedQuestions[subQuestionId].variation.options)
          ? updatedQuestions[subQuestionId].variation.options
          : JSON.parse(updatedQuestions[subQuestionId].variation.options);
        options[optIndex] = value;
        updatedQuestions[subQuestionId].variation.options = options;
      }
    }

    setEditingSetDetails({
      ...editingSetDetails,
      questions: updatedQuestions
    });
  };

  const getNextSelectionOrder = () => {
    const usedOrders = selectedSets.map(s => s.selection_order);
    for (let i = 1; i <= 10; i++) {
      if (!usedOrders.includes(i)) {
        return i;
      }
    }
    return null;
  };

  const handleDownloadSet = async (set, poolId) => {
    try {
      setDownloadingPDF(prev => ({ ...prev, [set.set_id]: true }));

      // Get pool and set details
      const poolData = finalizedPools.find(p => p.pool_paper_id === poolId);

      // Use edited details if available, otherwise use original set
      const setToDownload = editingSetDetails && selectedSetDetails && selectedSetDetails.set_id === set.set_id
        ? editingSetDetails
        : set;

      // Format the set content similar to moderator categorization
      const customContent = {
        header: `${poolData.university_name}\n\n${poolData.paper_title}`,
        instructions: `Set ${setToDownload.set_number} (Option ${setToDownload.selection_order})\nTotal Marks: ${setToDownload.total_marks}`,
        footer: 'End of Paper',
        questions: []
      };

      if (setToDownload.questions) {
        Object.entries(setToDownload.questions).forEach(([subQId, question]) => {
          const options = question.variation.options
            ? (Array.isArray(question.variation.options)
              ? question.variation.options
              : (typeof question.variation.options === 'string'
                ? JSON.parse(question.variation.options)
                : []))
            : null;

          customContent.questions.push({
            number: question.full_question_number || '',
            text: question.variation.question_text || '',
            type: question.variation.question_type || 'short_answer',
            options: options,
            answer: question.variation.correct_answer || '',
            marks: question.variation.marks || 0,
            difficulty: question.variation.difficulty_level || 'N/A'
          });
        });
      }

      // Show encryption modal
      setPendingDownloadData({
        setId: set.set_id,
        poolId: poolId,
        customContent: customContent,
        title: `${poolData.paper_title}_Set${setToDownload.set_number}`
      });
      setShowEncryptionModal(true);
      setEncryptionPassword('');
    } catch (err) {
      console.error('Error preparing set download:', err);
      showToast('Failed to prepare set for download', 'error');
    } finally {
      setDownloadingPDF(prev => ({ ...prev, [set.set_id]: false }));
    }
  };

  const confirmDownloadWithEncryption = async () => {
    if (pendingDownloadData) {
      // Validate password if provided
      if (encryptionPassword && encryptionPassword.trim().length > 0 && encryptionPassword.trim().length < 6) {
        showToast('Password must be at least 6 characters long', 'error');
        return;
      }

      try {
        setDownloadingPDF(prev => ({ ...prev, [pendingDownloadData.setId]: true }));

        showToast('🤖 AI is formatting your paper... This may take a few seconds.', 'info');

        // Call AI PDF generation endpoint for pool sets
        const response = await API.post(`/university-pool-coe/sets/${pendingDownloadData.setId}/download-pdf-ai`, {
          header: pendingDownloadData.customContent.header,
          instructions: pendingDownloadData.customContent.instructions,
          footer: pendingDownloadData.customContent.footer,
          questions: pendingDownloadData.customContent.questions,
          password: encryptionPassword || undefined
        }, {
          responseType: 'blob',
          timeout: 60000
        });

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const filename = encryptionPassword
          ? `${pendingDownloadData.title}_Encrypted.pdf`
          : `${pendingDownloadData.title}.pdf`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentElement.removeChild(link);
        window.URL.revokeObjectURL(url);

        if (encryptionPassword) {
          showToast('✅ Encrypted AI-formatted PDF downloaded successfully!', 'success');
        } else {
          showToast('✅ AI-formatted PDF downloaded successfully!', 'success');
        }

        setShowEncryptionModal(false);
        setPendingDownloadData(null);
        setEncryptionPassword('');
      } catch (err) {
        console.error('Download error:', err);
        showToast(err.response?.data?.message || 'Failed to download PDF', 'error');
      } finally {
        setDownloadingPDF(prev => ({ ...prev, [pendingDownloadData?.setId]: false }));
      }
    }
  };

  const cancelEncryptionModal = () => {
    setShowEncryptionModal(false);
    setPendingDownloadData(null);
    setEncryptionPassword('');
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="university-pool-coe-container">
      <div className="page-header">
        <h1>University Question Pool - COE</h1>
        <p>Review and select final question paper sets for university examinations</p>
        <div className="role-badge">
          <span className="badge coe-role">Controller of Examinations</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeTab === 'pools' ? 'active' : ''}`}
            onClick={() => setActiveTab('pools')}
          >
            Pending Papers ({poolsReady.length})
          </button>
          {selectedPool && activeTab !== 'finalized-sets' && (
            <button
              className={`tab-button ${activeTab === 'review' ? 'active' : ''}`}
              onClick={() => setActiveTab('review')}
            >
              Review & Select Sets
            </button>
          )}
          <button
            className={`tab-button ${activeTab === 'finalized' || activeTab === 'finalized-sets' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('finalized');
              setSelectedPool(null);
            }}
          >
            Finalized Papers ({finalizedPools.length})
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'pools' && (
            <div className="pools-section">
              <div className="section-header">
                <h2>Pending Papers ({poolsReady.length})</h2>
              </div>

              {poolsReady.length === 0 ? (
                <div className="empty-state">
                  <p>No pools ready for COE review</p>
                </div>
              ) : (
                <div className="pools-list">
                  {poolsReady.map(pool => (
                    <div key={pool.pool_paper_id} className="pool-list-item">
                      <div className="pool-item-content">
                        <div className="pool-item-header">
                          <h3 style={{ margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <Icon name="FileText" size={18} />
                            <span>{pool.subject_name} - {pool.paper_title || 'Pool Paper'}</span>
                          </h3>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '1rem',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              color: '#0066cc',
                              background: '#e6f2ff',
                              border: '1px solid #0066cc',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.3rem'
                            }}>
                              <Icon name="Building" size={12} /> University
                            </span>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '1rem',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              color: '#ff9800',
                              background: '#fff3e0',
                              border: '1px solid #ff9800',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.3rem'
                            }}>
                              <Icon name="Clock" size={12} /> Pending Review
                            </span>
                          </div>
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                          <p style={{ margin: '0.25rem 0' }}>From: {pool.university_name}</p>
                          <p style={{ margin: '0.25rem 0' }}>Sent: {new Date(pool.moderator_completed_at).toLocaleDateString()}</p>
                          <p style={{ margin: '0.25rem 0' }}>Available Sets: {pool.available_sets} | Selected: {pool.selected_sets}</p>
                        </div>
                      </div>
                      <button
                        className="btn btn-primary"
                        onClick={() => fetchPoolSets(pool.pool_paper_id)}
                        style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
                      >
                        <Icon name="Eye" size={16} /> View Pool
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'review' && selectedPool && (
            <div className="review-section">
              <div className="section-header">
                <h2>Review and Select Final Sets</h2>
                <p>Select the best sets for final examination use</p>
                <div className="review-actions">
                  <button
                    className="btn btn-success"
                    onClick={() => setShowFinalizeModal(true)}
                    disabled={selectedSets.length === 0}
                  >
                    Finalize Paper ({selectedSets.length} sets selected)
                  </button>
                </div>
              </div>

              {/* Selected Sets Summary - Grouped by Category */}
              {selectedSets.length > 0 && (
                <div className="selected-sets-summary">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Icon name="CheckCircle" size={18} />
                    <span>Selected Sets for Final Use ({selectedSets.length} total)</span>
                  </h3>

                  {/* Group selected sets by category */}
                  {(() => {
                    const categoryConfig = {
                      general: { icon: 'Book', label: 'General Exam', color: '#0066cc', bgColor: '#e6f2ff' },
                      special: { icon: 'Clipboard', label: 'Special Case', color: '#ff9900', bgColor: '#fff4e6' },
                      reexam: { icon: 'RotateCcw', label: 'Re-Examination', color: '#cc0000', bgColor: '#ffe6e6' }
                    };

                    const setsByCategory = {
                      general: selectedSets.filter(s => (s.category || availableSets.find(a => a.set_id === s.set_id)?.category) === 'general'),
                      special: selectedSets.filter(s => (s.category || availableSets.find(a => a.set_id === s.set_id)?.category) === 'special'),
                      reexam: selectedSets.filter(s => (s.category || availableSets.find(a => a.set_id === s.set_id)?.category) === 'reexam')
                    };

                    return (
                      <div style={{ display: 'grid', gap: '1.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                        {['general', 'special', 'reexam'].map(category => {
                          const config = categoryConfig[category];
                          const sets = setsByCategory[category];

                          return (
                            <div key={category} style={{
                              padding: '1.5rem',
                              background: config.bgColor,
                              borderRadius: '0.75rem',
                              border: `2px solid ${config.color}`
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                                <Icon name={config.icon} size={22} color={config.color} />
                                <h4 style={{ margin: 0, color: config.color, fontSize: '1.125rem', fontWeight: '700' }}>
                                  {config.label}
                                </h4>
                                <span style={{
                                  marginLeft: 'auto',
                                  background: config.color,
                                  color: 'white',
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '1rem',
                                  fontSize: '0.875rem',
                                  fontWeight: '600'
                                }}>
                                  {sets.length} set{sets.length !== 1 ? 's' : ''}
                                </span>
                              </div>

                              {sets.length === 0 ? (
                                <div style={{
                                  padding: '1rem',
                                  textAlign: 'center',
                                  color: 'black',
                                  fontSize: '0.875rem',
                                  fontStyle: 'italic'
                                }}>
                                  No sets selected for this category
                                </div>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                  {sets.map(selection => (
                                    <div key={selection.selection_id} style={{
                                      padding: '0.75rem 1rem',
                                      background: 'white',
                                      borderRadius: '0.5rem',
                                      border: `1px solid ${config.color}`,
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}>
                                      <div>
                                        <div style={{ fontWeight: '600', color: 'black' }}>
                                          Set {selection.set_number} (Option {selection.selection_order})
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: 'black', marginTop: '0.25rem' }}>
                                          {selection.total_marks} marks
                                        </div>
                                      </div>
                                      <button
                                        className="btn btn-danger btn-sm"
                                        onClick={() => handleUnselectSet(selection.set_id)}
                                        style={{ whiteSpace: 'nowrap' }}
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Categorized Sets Display */}
              <CategorizedSetsDisplay
                sets={availableSets}
                setsByCategory={setsByCategory}
                onSelectSet={handleSelectSet}
                onUnselectSet={handleUnselectSet}
                onViewDetails={viewSetDetails}
                selectedSets={selectedSets}
                isModeratorView={false}
              />
            </div>
          )}

          {activeTab === 'finalized' && (
            <div className="finalized-section">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Icon name="CheckCircle" size={20} />
                    <span>Finalized Papers ({finalizedPools.length})</span>
                  </h3>
                </div>

                {finalizedPools.length === 0 ? (
                  <div style={{ padding: '4rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    <p style={{ margin: '0 0 1rem 0', display: 'flex', justifyContent: 'center' }}><Icon name="CheckCircle" size={48} /></p>
                    <p style={{ margin: '0 0 0.5rem 0', fontSize: '1.125rem', fontWeight: '600' }}>No finalized papers yet</p>
                    <p style={{ margin: 0, fontSize: '0.875rem' }}>
                      Papers will appear here after you finalize them
                    </p>
                  </div>
                ) : (
                  <div style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'grid', gap: '1.25rem', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                      {finalizedPools.map(pool => (
                        <div
                          key={pool.pool_paper_id}
                          style={{
                            padding: '1.5rem',
                            // background: 'white',
                            background: 'var(--bg-secondary)',
                            borderRadius: '10px',
                            border: '2px solid var(--success)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem'
                          }}
                        >
                          {/* Header with title and badge */}
                          <div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.75rem' }}>
                              <h4 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '700', color: 'var(--text-primary)', flex: 1, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                <Icon name="FileText" size={18} />
                                <span>{pool.subject_name}</span>
                              </h4>
                              <span style={{
                                padding: '0.35rem 0.75rem',
                                borderRadius: '1rem',
                                fontSize: '0.75rem',
                                fontWeight: '700',
                                color: 'white',
                                background: 'var(--success)',
                                whiteSpace: 'nowrap',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.3rem'
                              }}>
                                <Icon name="Check" size={12} /> Finalized
                              </span>
                            </div>
                            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                              {pool.paper_title}
                            </p>
                          </div>

                          {/* Details */}
                          <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.9rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.5rem', borderBottom: '1px solid var(--border-color)' }}>
                              <span style={{ color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}><Icon name="Building" size={14} /> University:</span>
                              <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{pool.university_name}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.5rem', borderBottom: '1px solid var(--border-color)' }}>
                              <span style={{ color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}><Icon name="ChartBar" size={14} /> Total Marks:</span>
                              <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{pool.total_marks}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}><Icon name="Clipboard" size={14} /> Selected Sets:</span>
                              <span style={{ fontWeight: '700', color: 'var(--success)', fontSize: '1.1rem' }}>{pool.selected_sets}</span>
                            </div>
                          </div>

                          {/* Action Button */}
                          <button
                            className="btn btn-primary"
                            onClick={async () => {
                              setSelectedPool(pool.pool_paper_id);
                              try {
                                const { data } = await API.get(`/university-pool-coe/pools/${pool.pool_paper_id}/selected-sets`);
                                setSelectedSets(data.selections || []);
                              } catch (err) {
                                showToast('Failed to load sets', 'error');
                              }
                              setActiveTab('finalized-sets');
                            }}
                            style={{ width: '100%', marginTop: '0.5rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
                          >
                            <Icon name="Eye" size={16} /> View & Download Sets
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'finalized-sets' && selectedPool && (
            <div className="finalized-sets-section">
              <div className="card">
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Icon name="FileText" size={20} />
                    <span>Finalized Question Paper Sets</span>
                  </h3>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setActiveTab('finalized')}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
                    >
                      <Icon name="ArrowLeft" size={14} /> Back to Papers
                    </button>
                  </div>
                </div>
                <div style={{ padding: '1.5rem' }}>
                  <h4 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Icon name="Target" size={18} />
                    <span>Selected Sets for Final Examination (Category-wise)</span>
                  </h4>

                  {selectedSets.length === 0 ? (
                    <div className="empty-state">
                      <p>No sets available</p>
                    </div>
                  ) : (
                    <>
                      {(() => {
                        const categoryConfig = {
                          general: { icon: 'Book', label: 'General Exam', color: '#0066cc', bgColor: '#e6f2ff' },
                          special: { icon: 'Clipboard', label: 'Special Case', color: '#ff9900', bgColor: '#fff4e6' },
                          reexam: { icon: 'RotateCcw', label: 'Re-Examination', color: '#cc0000', bgColor: '#ffe6e6' }
                        };

                        const setsByCategory = {
                          general: selectedSets.filter(s => (s.category || availableSets.find(a => a.set_id === s.set_id)?.category) === 'general'),
                          special: selectedSets.filter(s => (s.category || availableSets.find(a => a.set_id === s.set_id)?.category) === 'special'),
                          reexam: selectedSets.filter(s => (s.category || availableSets.find(a => a.set_id === s.set_id)?.category) === 'reexam')
                        };

                        return (
                          <div style={{ display: 'grid', gap: '2rem', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))' }}>
                            {['general', 'special', 'reexam'].map(category => {
                              const config = categoryConfig[category];
                              const sets = setsByCategory[category];

                              return (
                                <div key={category} style={{
                                  padding: '1.5rem',
                                  background: config.bgColor,
                                  borderRadius: '0.75rem',
                                  border: `2px solid ${config.color}`
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                                    <Icon name={config.icon} size={26} color={config.color} />
                                    <div>
                                      <h5 style={{ margin: 0, color: config.color, fontSize: '1.125rem', fontWeight: '700' }}>
                                        {config.label}
                                      </h5>
                                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: 'black' }}>
                                        {sets.length} set{sets.length !== 1 ? 's' : ''}
                                      </p>
                                    </div>
                                  </div>

                                  {sets.length === 0 ? (
                                    <div style={{
                                      padding: '1.5rem',
                                      textAlign: 'center',
                                      background: 'white',
                                      borderRadius: '0.5rem',
                                      color: 'black',
                                      fontSize: '0.875rem',
                                      fontStyle: 'italic'
                                    }}>
                                      No sets selected for this category
                                    </div>
                                  ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                      {sets.map(set => (
                                        <div key={set.selection_id} style={{
                                          padding: '1rem',
                                          background: 'white',
                                          borderRadius: '0.5rem',
                                          border: `2px solid ${config.color}`,
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: '0.75rem'
                                        }}>
                                          <div>
                                            <div style={{ fontWeight: '700', fontSize: '1.125rem', color: config.color, marginBottom: '0.25rem' }}>
                                              Set {set.set_number}
                                            </div>
                                            <div style={{ fontSize: '0.875rem', color: 'black' }}>
                                              Option {set.selection_order} • {set.total_marks} marks
                                            </div>
                                          </div>
                                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <button
                                              className="btn btn-primary btn-sm"
                                              onClick={() => viewSetDetails(set.set_id)}
                                              style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }}
                                            >
                                              <Icon name="Edit" size={14} /> Preview & Edit
                                            </button>
                                            <button
                                              className="btn btn-success btn-sm"
                                              onClick={() => {
                                                // Use edited details if modal is open and set matches
                                                const setToDownload = editingSetDetails && selectedSetDetails && selectedSetDetails.set_id === set.set_id
                                                  ? editingSetDetails
                                                  : set;
                                                handleDownloadSet(setToDownload, selectedPool);
                                              }}
                                              disabled={downloadingPDF[set.set_id]}
                                              style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }}
                                            >
                                              {downloadingPDF[set.set_id]
                                                ? <><span className="inline-spinner"></span>Preparing...</>
                                                : <><Icon name="Download" size={14} /> Download PDF</>}
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Encryption Modal */}
      {showEncryptionModal && (() => {
        const isDownloading = pendingDownloadData ? !!downloadingPDF[pendingDownloadData.setId] : false;
        const passwordTooShort = encryptionPassword && encryptionPassword.trim().length > 0 && encryptionPassword.trim().length < 6;

        return (
          <div className="modal-overlay" onClick={() => { if (!isDownloading) cancelEncryptionModal(); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Icon name="Lock" size={18} />
                  <span>PDF Encryption Options</span>
                </h3>
                <button
                  className="modal-close"
                  onClick={() => cancelEncryptionModal()}
                  disabled={isDownloading}
                >
                  ×
                </button>
              </div>

              <div className="modal-body">
                <p>Enter a password to encrypt the PDF (optional):</p>
                <input
                  type="password"
                  placeholder="Leave empty for no encryption"
                  value={encryptionPassword}
                  onChange={(e) => setEncryptionPassword(e.target.value)}
                  disabled={isDownloading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: '1px solid var(--border-color)',
                    marginBottom: '1rem'
                  }}
                />
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Icon name="Lightbulb" size={14} /> Tip: Use a strong password (min. 6 characters)
                </p>
                {encryptionPassword && encryptionPassword.length > 0 && (
                  <span style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: encryptionPassword.length >= 6 ? 'var(--success)' : 'var(--danger)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    {encryptionPassword.length >= 6 ? <><Icon name="Check" size={12} /> Valid</> : `${encryptionPassword.length}/6 chars`}
                  </span>
                )}

                {isDownloading && (
                  <div className="download-progress">
                    <span className="inline-spinner"></span>
                    AI is formatting your paper and preparing the download. Please wait...
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => cancelEncryptionModal()}
                  disabled={isDownloading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success"
                  onClick={() => confirmDownloadWithEncryption()}
                  disabled={isDownloading || passwordTooShort}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
                >
                  {isDownloading
                    ? <><span className="inline-spinner"></span>Generating PDF...</>
                    : (encryptionPassword
                      ? <><Icon name="Lock" size={16} /> Download Encrypted</>
                      : <><Icon name="Download" size={16} /> Download</>)}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Finalize Paper Modal */}
      {showFinalizeModal && (
        <div className="modal-overlay" onClick={() => setShowFinalizeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Finalize Paper</h3>
              <button
                className="modal-close"
                onClick={() => setShowFinalizeModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p>Are you sure you want to finalize this paper? This action cannot be undone.</p>

              <div className="finalization-summary">
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Icon name="CheckCircle" size={16} />
                  <span>Selected Sets for Final Use (Category-wise):</span>
                </h4>

                {(() => {
                  const categoryConfig = {
                    general: { icon: 'Book', label: 'General Exam', color: '#0066cc', bgColor: '#e6f2ff' },
                    special: { icon: 'Clipboard', label: 'Special Case', color: '#ff9900', bgColor: '#fff4e6' },
                    reexam: { icon: 'RotateCcw', label: 'Re-Examination', color: '#cc0000', bgColor: '#ffe6e6' }
                  };

                  const setsByCategory = {
                    general: selectedSets.filter(s => (s.category || availableSets.find(a => a.set_id === s.set_id)?.category) === 'general'),
                    special: selectedSets.filter(s => (s.category || availableSets.find(a => a.set_id === s.set_id)?.category) === 'special'),
                    reexam: selectedSets.filter(s => (s.category || availableSets.find(a => a.set_id === s.set_id)?.category) === 'reexam')
                  };

                  return (
                    <div style={{ display: 'grid', gap: '1rem', marginTop: '1rem' }}>
                      {['general', 'special', 'reexam'].map(category => {
                        const config = categoryConfig[category];
                        const sets = setsByCategory[category];

                        return (
                          <div key={category} style={{
                            padding: '1rem',
                            background: config.bgColor,
                            borderRadius: '0.5rem',
                            border: `2px solid ${config.color}`
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                              <Icon name={config.icon} size={18} color={config.color} />
                              <h5 style={{ margin: 0, color: config.color, fontWeight: '700' }}>
                                {config.label}
                              </h5>
                              <span style={{
                                marginLeft: 'auto',
                                background: config.color,
                                color: 'white',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}>
                                {sets.length} set{sets.length !== 1 ? 's' : ''}
                              </span>
                            </div>

                            {sets.length === 0 ? (
                              <div style={{
                                padding: '0.5rem',
                                color: 'var(--text-secondary)',
                                fontSize: '0.875rem',
                                fontStyle: 'italic'
                              }}>
                                No sets selected
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {sets.map(selection => (
                                  <div key={selection.selection_id} style={{
                                    padding: '0.5rem 0.75rem',
                                    background: 'white',
                                    borderRadius: '0.25rem',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    fontSize: '0.875rem'
                                  }}>
                                    <span>
                                      <strong>Option {selection.selection_order}:</strong> Set {selection.set_number}
                                    </span>
                                    <span style={{ color: 'var(--text-secondary)' }}>
                                      {selection.total_marks} marks
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              <div className="finalization-warning">
                <p><strong>After finalization:</strong></p>
                <ul>
                  <li>No further changes can be made to this paper</li>
                  <li>Selected sets will be available for download</li>
                  <li>Paper will be marked as completed</li>
                  <li>All workflow stages will be locked</li>
                </ul>
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowFinalizeModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={handleFinalizePaper}
              >
                Finalize Paper
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Set Details Modal */}
      {showSetModal && selectedSetDetails && editingSetDetails && (
        <div className="modal-overlay" onClick={() => setShowSetModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                <h3 style={{ margin: 0 }}>
                  Set {selectedSetDetails.set_number} - {isEditingSet
                    ? <><Icon name="Edit" size={15} style={{ verticalAlign: '-2px' }} /> Edit Mode</>
                    : 'Preview'}
                </h3>
                {!isEditingSet && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setIsEditingSet(true)}
                    style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                  >
                    <Icon name="Edit" size={14} /> Edit Questions
                  </button>
                )}
                {isEditingSet && (
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setIsEditingSet(false);
                        setEditingSetDetails(JSON.parse(JSON.stringify(selectedSetDetails)));
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => {
                        setSelectedSetDetails(editingSetDetails);
                        setIsEditingSet(false);
                        showToast('Changes saved locally', 'success');
                      }}
                    >
                      Save Changes
                    </button>
                  </div>
                )}
              </div>
              <button
                className="modal-close"
                onClick={() => {
                  // Save any pending edits before closing
                  if (isEditingSet && editingSetDetails) {
                    setSelectedSetDetails(editingSetDetails);
                  }
                  setShowSetModal(false);
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="set-info">
                <div className="info-grid">
                  <div className="info-item" style={{}}>
                    <strong>Total Marks:</strong> {selectedSetDetails.total_marks}
                  </div>
                  <div className="info-item">
                    <strong>Status:</strong> {selectedSetDetails.status}
                  </div>
                  <div className="info-item">
                    <strong>Created by:</strong> {selectedSetDetails.created_by_name}
                  </div>
                  <div className="info-item">
                    <strong>Created:</strong> {new Date(selectedSetDetails.created_at).toLocaleDateString()}
                  </div>
                  {selectedSetDetails.selection_order && (
                    <div className="info-item">
                      <strong>Selection Order:</strong> Option {selectedSetDetails.selection_order}
                    </div>
                  )}
                  {selectedSetDetails.coe_comments && (
                    <div className="info-item">
                      <strong>COE Comments:</strong> {selectedSetDetails.coe_comments}
                    </div>
                  )}
                </div>
              </div>

              <div className="questions-list">
                <h4>Questions in this Set:</h4>
                {Object.entries(editingSetDetails.questions || {}).map(([subQuestionId, questionData]) => (
                  <div key={subQuestionId} style={{
                    padding: '1rem',
                    // background: isEditingSet ? 'var(--bg-secondary)' : 'white',
                    borderRadius: '0.5rem',
                    border: '1px solid var(--border-color)',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <div>
                        <h5 style={{ margin: '0 0 0.25rem 0' }}>{questionData.full_question_number}</h5>
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          <span style={{ marginRight: '1rem' }}>Variation #{questionData.variation.variation_number}</span>
                          <span style={{ marginRight: '1rem' }}>Quality: {questionData.variation.quality_score}</span>
                          <span>{questionData.variation.marks} marks</span>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {/* Question Text */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
                          Question Text:
                        </label>
                        {isEditingSet ? (
                          <textarea
                            value={questionData.variation.question_text}
                            onChange={(e) => handleEditSetQuestion(subQuestionId, 'question_text', e.target.value)}
                            dir="auto"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              borderRadius: '0.375rem',
                              border: '1px solid var(--border-color)',
                              background: 'var(--bg-secondary)',
                              color: 'var(--text-primary)',
                              fontSize: '0.875rem',
                              fontFamily: 'system-ui, -apple-system, "Segoe UI", "Noto Sans", "Noto Sans Devanagari", "Noto Sans Arabic", sans-serif',
                              resize: 'vertical',
                              minHeight: '60px',
                              lineHeight: '1.5'
                            }}
                          />
                        ) : (
                          <p style={{ margin: '0.5rem 0', lineHeight: '1.5' }}>{questionData.variation.question_text}</p>
                        )}
                      </div>

                      {/* Options */}
                      {questionData.variation.options && (
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
                            Options:
                          </label>
                          {(() => {
                            const options = Array.isArray(questionData.variation.options)
                              ? questionData.variation.options
                              : JSON.parse(questionData.variation.options);
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {options.map((option, optIndex) => (
                                  <div key={optIndex} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <span style={{ fontSize: '0.875rem', fontWeight: '600', minWidth: '30px' }}>
                                      {String.fromCharCode(65 + optIndex)}.
                                    </span>
                                    {isEditingSet ? (
                                      <input
                                        type="text"
                                        value={option}
                                        onChange={(e) => handleEditSetQuestion(subQuestionId, `option_${optIndex}`, e.target.value)}
                                        dir="auto"
                                        style={{
                                          flex: 1,
                                          padding: '0.375rem 0.5rem',
                                          borderRadius: '0.25rem',
                                          border: '1px solid var(--border-color)',
                                          background: 'var(--bg-secondary)',
                                          color: 'var(--text-primary)',
                                          fontSize: '0.875rem',
                                          fontFamily: 'system-ui, -apple-system, "Segoe UI", "Noto Sans", "Noto Sans Devanagari", "Noto Sans Arabic", sans-serif'
                                        }}
                                      />
                                    ) : (
                                      <span style={{ flex: 1 }}>{option}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Correct Answer */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
                          Correct Answer:
                        </label>
                        {isEditingSet ? (
                          <textarea
                            value={questionData.variation.correct_answer}
                            onChange={(e) => handleEditSetQuestion(subQuestionId, 'correct_answer', e.target.value)}
                            dir="auto"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              borderRadius: '0.375rem',
                              border: '1px solid var(--border-color)',
                              background: 'var(--bg-secondary)',
                              color: 'var(--text-primary)',
                              fontSize: '0.875rem',
                              fontFamily: 'system-ui, -apple-system, "Segoe UI", "Noto Sans", "Noto Sans Devanagari", "Noto Sans Arabic", sans-serif',
                              resize: 'vertical',
                              minHeight: '50px',
                              lineHeight: '1.5'
                            }}
                          />
                        ) : (
                          <p style={{ margin: '0.5rem 0', padding: '0.5rem', background: 'var(--success-light)', borderRadius: '0.25rem', lineHeight: '1.5' }}>
                            {questionData.variation.correct_answer}
                          </p>
                        )}
                      </div>

                      {/* Marks */}
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
                          Marks:
                        </label>
                        {isEditingSet ? (
                          <input
                            type="number"
                            value={questionData.variation.marks}
                            onChange={(e) => handleEditSetQuestion(subQuestionId, 'marks', parseInt(e.target.value) || 0)}
                            min="1"
                            style={{
                              width: '80px',
                              padding: '0.375rem 0.5rem',
                              borderRadius: '0.25rem',
                              border: '1px solid var(--border-color)',
                              background: 'var(--bg-secondary)',
                              color: 'var(--text-primary)',
                              fontSize: '0.875rem'
                            }}
                          />
                        ) : (
                          <span style={{ fontWeight: '600' }}>{questionData.variation.marks}</span>
                        )}
                      </div>

                      {/* Difficulty */}
                      {questionData.variation.difficulty_level && (
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          <strong>Difficulty:</strong> {questionData.variation.difficulty_level}
                          {questionData.variation.difficulty_reason && (
                            <span> - {questionData.variation.difficulty_reason}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowSetModal(false)}
              >
                Close
              </button>
              {!selectedSetDetails.is_selected && (
                <button
                  className="btn btn-success"
                  onClick={() => {
                    const order = getNextSelectionOrder();
                    if (order) {
                      setPendingSetSelection({ setId: selectedSetDetails.set_id, order });
                      setShowCommentsModal(true);
                    } else {
                      showToast('Maximum 10 sets can be selected', 'error');
                    }
                  }}
                  disabled={!getNextSelectionOrder()}
                >
                  Select as Option {getNextSelectionOrder() || 'N/A'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comments Modal */}
      {showCommentsModal && (
        <div className="modal-overlay" onClick={() => setShowCommentsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Comments (Optional)</h3>
              <button
                className="modal-close"
                onClick={() => setShowCommentsModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                Add optional comments for this set selection:
              </p>
              <textarea
                className="form-input"
                value={commentsText}
                onChange={(e) => setCommentsText(e.target.value)}
                placeholder="Enter your comments here..."
                rows="4"
                style={{ resize: 'vertical', minHeight: '100px' }}
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowCommentsModal(false);
                  setCommentsText('');
                  setPendingSetSelection(null);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={() => {
                  if (pendingSetSelection) {
                    handleSelectSet(pendingSetSelection.setId, pendingSetSelection.order, commentsText);
                    setShowSetModal(false);
                    setShowCommentsModal(false);
                    setCommentsText('');
                    setPendingSetSelection(null);
                  }
                }}
              >
                Confirm Selection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversityPoolCOE;